/**
 * Report card comments: a template language that keeps pronouns and verbs agreeing.
 *
 * Templates are sentences with tokens:
 *
 * - `{S}` opens a comment: the student's name, or the capitalized subject pronoun.
 * - `{s}` is the subject pronoun later in a sentence ("as soon as {s} …").
 * - `{obj}`, `{poss}`, `{Poss}`, `{refl}`: them/her/him, their/her/his, themselves/…
 * - `{subject}`: the course, or a fallback such as "class".
 * - `{works|work}`: a verb that agrees with the most recent subject — the first form
 *   after a name or she/he, the second after "they". "Alex is" but "They are", and
 *   "Alex is proud of their work" for a student who uses they/them.
 *
 * Agreement follows the subject actually printed, not the pronoun chosen, which is the
 * whole difficulty: a they/them student's comment opens "Alex is …" (singular, a name)
 * and continues "They are …" (plural form) — so each subject token resets it.
 */

export type Pronoun = "they" | "she" | "he";

export type CommentCategory =
  | "achievement"
  | "effort"
  | "participation"
  | "behavior"
  | "growth"
  | "next-steps";

export type CommentLevel = "excellent" | "satisfactory" | "needs-improvement";

export const COMMENT_CATEGORIES: readonly CommentCategory[] = [
  "achievement",
  "effort",
  "participation",
  "behavior",
  "growth",
  "next-steps",
];

export const COMMENT_LEVELS: readonly CommentLevel[] = [
  "excellent",
  "satisfactory",
  "needs-improvement",
];

/** The bank key for a category at a level, e.g. "effort-excellent". */
export function bankKey(
  category: CommentCategory,
  level: CommentLevel
): string {
  return `${category}-${level}`;
}

/** subject, object, possessive, reflexive — e.g. ["they", "them", "their", "themselves"]. */
export type PronounForms = readonly string[];

export interface CommentContext {
  name: string;
  /** Used when no name is entered, e.g. "This student". */
  nameFallback: string;
  pronoun: Pronoun;
  forms: PronounForms;
  subject: string;
  /** Used when no subject is entered, e.g. "class". */
  subjectFallback: string;
  /** Print the name for `{S}` (otherwise the pronoun). */
  useName: boolean;
  locale?: string;
}

const TOKEN = /\{([^{}]+)\}/g;
const KNOWN_TOKENS = new Set([
  "S",
  "s",
  "obj",
  "poss",
  "Poss",
  "refl",
  "subject",
]);

function capitalize(word: string, locale?: string): string {
  return word.charAt(0).toLocaleUpperCase(locale) + word.slice(1);
}

function pronounToken(
  token: string,
  context: CommentContext
): string | undefined {
  const [subjectForm = "", object = "", possessive = "", reflexive = ""] =
    context.forms;
  const values: Record<string, string> = {
    s: subjectForm,
    obj: object,
    poss: possessive,
    Poss: capitalize(possessive, context.locale),
    refl: reflexive,
    subject: context.subject.trim() || context.subjectFallback,
  };
  return values[token];
}

/** One template with the student's name, pronouns and subject filled in. */
export function renderComment(
  template: string,
  context: CommentContext
): string {
  const pluralPronoun = context.pronoun === "they";
  let plural = pluralPronoun;
  return template.replace(TOKEN, (match, token: string) => {
    const bar = token.indexOf("|");
    if (bar >= 0) {
      return plural ? token.slice(bar + 1) : token.slice(0, bar);
    }
    if (token === "S") {
      if (context.useName) {
        plural = false;
        return context.name.trim() || context.nameFallback;
      }
      plural = pluralPronoun;
      return capitalize(context.forms[0] ?? "", context.locale);
    }
    if (token === "s") {
      plural = pluralPronoun;
    }
    return pronounToken(token, context) ?? match;
  });
}

/**
 * Several templates as one paragraph. The first comment opens with the name; the rest
 * open with the pronoun, unless `nameEvery` asks for the name each time (useful when a
 * report system has a separate box per category).
 */
export function assembleComments(
  templates: readonly string[],
  context: Omit<CommentContext, "useName">,
  nameEvery: boolean
): string {
  return templates
    .map((template, index) =>
      renderComment(template, { ...context, useName: nameEvery || index === 0 })
    )
    .join(" ");
}

/** Problems with a template: an unknown token, or one that does not open with {S}. */
export function templateProblems(template: string): string[] {
  const problems: string[] = [];
  if (!template.startsWith("{S}")) {
    problems.push("does not start with {S}");
  }
  for (const [, token] of template.matchAll(TOKEN)) {
    const isAgreement = (token ?? "").split("|").length === 2;
    if (!(isAgreement || KNOWN_TOKENS.has(token ?? ""))) {
      problems.push(`unknown token {${token}}`);
    }
  }
  return problems;
}
